import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import BlogPostScheduler from './BlogPostScheduler';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatDateInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
};

const formatDateDisplay = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-US', {
      dateStyle: 'medium', timeStyle: 'short',
    });
  } catch {
    return iso;
  }
};

export const computeStatus = (post) => {
  if (post.status === 'draft') return 'draft';
  return new Date(post.publish_at) <= new Date() ? 'published' : 'scheduled';
};

const STATUS_BADGE = {
  published: 'bg-green-100 text-green-700',
  scheduled: 'bg-blue-100 text-blue-700',
  draft: 'bg-gray-200 text-gray-700',
};

const StatusBadge = ({ status }) => (
  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[status]}`}>
    {status}
  </span>
);

const BlogPostRow = ({ post, authedFetch, onUpdated }) => {
  const liveStatus = computeStatus(post);

  const handleSave = async (changes) => {
    try {
      await authedFetch({
        url: `${BACKEND_URL}/api/admin/blog/posts/${post.slug}`,
        method: 'PATCH',
        data: changes,
      });
      toast.success('Post updated');
      onUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    }
  };

  return (
    <Card className="border-2" data-testid="admin-blog-row">
      <CardContent className="p-5 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <StatusBadge status={liveStatus} />
              <span className="text-xs text-gray-500">
                {formatDateDisplay(post.publish_at)}
              </span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 truncate">{post.title}</h3>
            <p className="text-xs text-gray-500 mt-1 truncate">/{post.slug}</p>
          </div>
          {liveStatus === 'published' && (
            <Link
              to={`/blog/${post.slug}`}
              className="text-blue-600 hover:underline text-sm flex items-center"
              target="_blank" rel="noreferrer"
            >
              View <ExternalLink className="w-3 h-3 ml-1" />
            </Link>
          )}
        </div>

        <BlogPostScheduler
          post={post}
          formatDateInput={formatDateInput}
          onSave={handleSave}
        />
      </CardContent>
    </Card>
  );
};

export default BlogPostRow;
